import React, { useRef, useEffect, useState, useContext, useCallback } from 'react';
import { GameContext } from '../contexts/GameContext';
import { GAME_STATE, TEAMS } from '../utils/constants';
import UnitTooltip from './UnitTooltip';
import BattleLine from './BattleLine';
import { UNIT_TYPES } from '../models/UnitTypes';

const Battlefield = () => {
  const { 
    units, 
    gameState, 
    winner, 
    deploymentGrid,
    hoveredCell,
    selectedUnitType,
    battleLinePosition,
    setHoveredCell,
    placeUnit,
    deleteUnit,
    setCanvasRef
  } = useContext(GameContext);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 400 });
  const [inspectedUnit, setInspectedUnit] = useState(null);
  
  // Responsive canvas sizing
  const handleResize = useCallback(() => {
    if (!containerRef.current) return;
    
    const containerWidth = containerRef.current.clientWidth;
    const aspectRatio = 2.25;
    const canvasHeight = containerWidth / aspectRatio;
    
    const finalWidth = Math.floor(containerWidth);
    const finalHeight = Math.floor(canvasHeight);
    
    setDimensions({
      width: finalWidth,
      height: finalHeight
    });
    
    if (setCanvasRef) {
      setCanvasRef(finalWidth, finalHeight);
    }
  }, [setCanvasRef]);
  
  // Initialize and handle resizing
  useEffect(() => {
    handleResize();
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  
  // Handle canvas click
  const handleCanvasClick = useCallback((e) => {
    const canvas = e.target;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (gameState === GAME_STATE.PLACEMENT) {
      // Only allow placing units in deployment zone (left 25% of screen)
      const deploymentZoneWidth = canvas.width * 0.25;
      if (x >= deploymentZoneWidth) return;
      
      // Find the nearest grid cell
      let nearestCell = null;
      let nearestDistance = Infinity;
      
      deploymentGrid.forEach(cell => {
        if (!cell.occupied) {
          const distance = Math.hypot(cell.x - x, cell.y - y);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestCell = cell;
          }
        }
      });
      
      if (nearestCell) {
        placeUnit(nearestCell);
      }
    }
  }, [gameState, deploymentGrid, placeUnit]);
  
  // Handle mouse move for unit hover
  const handleMouseMove = useCallback((e) => {
    const canvas = e.target;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (gameState === GAME_STATE.PLACEMENT) {
      // Only track hover in deployment zone
      const deploymentZoneWidth = canvas.width * 0.25;
      if (x >= deploymentZoneWidth) {
        setHoveredCell(null);
        return;
      }
      
      // Find nearest available cell
      let nearest = null;
      let nearestDistance = Infinity;
      
      deploymentGrid.forEach(cell => {
        if (!cell.occupied) {
          const distance = Math.hypot(cell.x - x, cell.y - y);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = cell;
          }
        }
      });
      
      setHoveredCell(nearest);
    }
    
    // Handle unit inspection
    let inspected = null;
    for (const team of [TEAMS.RED, TEAMS.BLUE]) {
      for (const unit of units[team]) {
        if (unit.containsPoint && unit.containsPoint(x, y)) {
          inspected = unit;
          break;
        }
      }
      if (inspected) break;
    }
    setInspectedUnit(inspected);
  }, [gameState, deploymentGrid, setHoveredCell, units]);
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Get current mouse position relative to canvas
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      
      // Get position from last known mouse event if available
      if (e.clientX && e.clientY) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Delete unit with 'x' key during placement
        if ((e.key === 'x' || e.key === 'X') && gameState === GAME_STATE.PLACEMENT) {
          deleteUnit(x, y);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [gameState, deleteUnit]);
  
  // Draw the battlefield
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw battlefield background
    ctx.fillStyle = '#88CC88'; // Green field
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw deployment zone
    if (gameState === GAME_STATE.PLACEMENT) {
      const deploymentZoneWidth = canvas.width * 0.25;
      
      ctx.fillStyle = 'rgba(0, 128, 0, 0.2)';
      ctx.fillRect(0, 0, deploymentZoneWidth, canvas.height);
      ctx.strokeStyle = '#22c55e';
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(deploymentZoneWidth, 0);
      ctx.lineTo(deploymentZoneWidth, canvas.height);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw deployment grid
      if (deploymentGrid && deploymentGrid.length > 0) {
        deploymentGrid.forEach(cell => {
          // Recalculate cell position based on current canvas dimensions
          const deploymentZoneWidth = canvas.width * 0.25;
          const x = deploymentZoneWidth * cell.xPercent;
          const y = canvas.height * cell.yPercent;
          
          // Calculate cell size
          const cellWidth = deploymentZoneWidth * cell.cellWidthPercent;
          const cellHeight = canvas.height * cell.cellHeightPercent;
          
          // Set cell style based on occupation
          ctx.strokeStyle = cell.occupied ? '#FF9900' : '#22c55e';
          ctx.lineWidth = 2;
          
          // Draw cell rectangle
          ctx.strokeRect(
            x - cellWidth/2, 
            y - cellHeight/2,
            cellWidth,
            cellHeight
          );
          
          // Highlight hovered cell
          if (hoveredCell && cell.row === hoveredCell.row && cell.col === hoveredCell.col) {
            ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
            ctx.fillRect(
              x - cellWidth/2, 
              y - cellHeight/2,
              cellWidth,
              cellHeight
            );
            
            // Show selected unit preview in hovered cell
            if (selectedUnitType && UNIT_TYPES[selectedUnitType]) {
              const unitInfo = UNIT_TYPES[selectedUnitType];
              ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
              ctx.font = '24px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(unitInfo.emoji, x, y);
            }
          }
          
          // Show unit type in occupied cells
          if (cell.occupied && cell.unitType) {
            const unitInfo = UNIT_TYPES[cell.unitType];
            if (unitInfo && unitInfo.emoji) {
              ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
              ctx.beginPath();
              ctx.arc(x, y, Math.min(cellWidth, cellHeight) * 0.2, 0, Math.PI * 2);
              ctx.fill();
              
              ctx.font = '20px Arial';
              ctx.fillStyle = '#FFFFFF';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(unitInfo.emoji, x, y);
            }
          }
        });
      }
    }
    
    // Draw enemy base zone
    ctx.fillStyle = 'rgba(220, 50, 50, 0.2)';
    ctx.fillRect(canvas.width * 0.9, 0, canvas.width * 0.1, canvas.height);
    
    // Draw units
    for (const team of [TEAMS.RED, TEAMS.BLUE]) {
      for (const unit of units[team]) {
        if (unit.health <= 0) continue;
        
        // Scale units based on canvas size
        const baseUnitSize = 24;
        const scaleFactor = Math.min(canvas.width / 900, canvas.height / 400);
        const scaledSize = (unit.size || baseUnitSize) * scaleFactor;
        
        // Draw unit circle
        ctx.fillStyle = team === TEAMS.RED ? '#FF0000' : '#0000FF';
        ctx.beginPath();
        ctx.arc(unit.x, unit.y, scaledSize / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw unit emoji
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `${Math.max(14, scaledSize)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(unit.emoji || '👤', unit.x, unit.y);
        
        // Draw health bar
        const healthPct = unit.health / unit.maxHealth;
        const barWidth = scaledSize * 1;
        const barHeight = Math.max(2, scaledSize * 0.2);
        
        ctx.fillStyle = '#000';
        ctx.fillRect(unit.x - barWidth/2, unit.y - scaledSize - 3, barWidth, barHeight);
        
        ctx.fillStyle = healthPct > 0.5 ? '#0F0' : healthPct > 0.25 ? '#FF0' : '#F00';
        ctx.fillRect(unit.x - barWidth/2, unit.y - scaledSize - 3, barWidth * healthPct, barHeight);
        
        // Structure indicator
        if (unit.isStructure) {
          ctx.strokeStyle = unit.team === TEAMS.RED ? '#FF9900' : '#6666FF';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(unit.x, unit.y, scaledSize + 5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.lineWidth = 1;
        }
      }
    }
    
  }, [gameState, units, winner, dimensions, deploymentGrid, hoveredCell, selectedUnitType]);
  
  return (
    <div className="w-full relative" ref={containerRef}>
      <canvas 
        ref={canvasRef} 
        width={dimensions.width} 
        height={dimensions.height} 
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        className="border border-gray-700 rounded-lg shadow-lg mx-auto block"
      />
      
      <BattleLine position={battleLinePosition} height={dimensions.height} />
      
      {inspectedUnit && (
        <UnitTooltip 
          unit={inspectedUnit} 
          style={{
            left: `${inspectedUnit.x + 20}px`,
            top: `${inspectedUnit.y - 20}px`
          }}
        />
      )}
      
      {/* Grid layout indicator text - shown during placement phase */}
      {gameState === GAME_STATE.PLACEMENT && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white p-2 rounded text-xs z-10">
          <p><strong>Deployment Zone</strong><br/>Deploy units in the green area</p>
          <p className="mt-1"><strong>Controls:</strong> Click to place units, press X to delete</p>
        </div>
      )}
    </div>
  );
};

export default Battlefield;